import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  CreatePaymentPlanDto,
  PaymentPlanSetupMethod,
  paymentPlansApi,
  formatCurrency,
  formatDate,
  handleApiError
} from '../../services/paymentsService';
import { DiscountDto, DiscountType } from '../../services/discountsService';

interface CreatePaymentPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId: number;
  clientName: string;
  suggestedAmount?: number; // יתרה לאחר הנחה
  discounts?: DiscountDto[]; // הנחות הלקוח — להארכת הנחה חודשית עד סוף התוכנית
  vatRate?: number;
}

const toDateKey = (value: string | Date): string => {
  const d = typeof value === 'string' ? new Date(value) : value;
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

// מספר החודשים שבין חודש הסיום הנוכחי לחודש הסיום החדש — כמו MonthsInclusive בשרת
const monthsBetween = (from: string, to: string): number => {
  const a = new Date(from);
  const b = new Date(to);
  return Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()));
};

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div<{ $wide?: boolean }>`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: ${props => props.$wide ? '900px' : '600px'};
  max-height: 95vh;
  overflow-y: auto;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const ClientBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: var(--primary-light);
  color: var(--primary-color);
  border-radius: var(--border-radius-md);
  font-size: 12px;
  font-weight: 600;
  margin-right: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
`;

const RequiredStar = styled.span`
  color: var(--error-color);
  margin-right: 4px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  min-height: 60px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const HelperText = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  margin-top: 4px;
`;

const ExtendDiscountBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 20px;
  border: 1px solid rgba(147, 51, 234, 0.3);
  border-radius: var(--border-radius-md);
  background: rgba(147, 51, 234, 0.05);
  cursor: pointer;

  input {
    margin-top: 3px;
    accent-color: #9333ea;
  }
`;

const ExtendDiscountText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  color: var(--text-primary);

  strong {
    font-weight: 600;
  }

  span {
    font-size: 12px;
    color: var(--text-secondary);
  }
`;

const CalculatedValue = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: var(--primary-light);
  border-radius: var(--border-radius-md);
  border-right: 4px solid var(--primary-color);
`;

const CalculatedLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 4px;
`;

const CalculatedAmount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--primary-color);
  direction: ltr;
  text-align: right;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'secondary' ? 'var(--border-color)' : 'var(--primary-color)'};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Radio cards for setup method selection
const SetupMethodGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
`;

const SetupMethodCard = styled.label<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  border: 2px solid ${props => props.$selected ? 'var(--primary-color)' : 'var(--border-color)'};
  background: ${props => props.$selected ? 'var(--primary-light)' : 'white'};
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary-color);
  }

  input[type='radio'] {
    margin-left: 8px;
  }
`;

const SetupMethodTitle = styled.div`
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const SetupMethodDesc = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

// iframe view
const IframeContainer = styled.div`
  padding: 0;
`;

const IframeBanner = styled.div`
  padding: 16px 24px;
  background: var(--primary-light);
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  font-size: 14px;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 700px;
  border: none;
  display: block;
`;

const StatusBox = styled.div`
  padding: 24px;
  text-align: center;
`;

const Spinner = styled.div`
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

type ViewState = 'form' | 'iframe' | 'finalizing' | 'success';

const CreatePaymentPlanModal: React.FC<CreatePaymentPlanModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clientId,
  clientName,
  suggestedAmount,
  discounts,
  vatRate
}) => {
  const [loading, setLoading] = useState(false);
  const [extendDiscounts, setExtendDiscounts] = useState(false);
  const [totalAmountTouched, setTotalAmountTouched] = useState(false);
  const [view, setView] = useState<ViewState>('form');
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [, setCurrentPlanId] = useState<number | null>(null);
  const pollingRef = useRef<number | null>(null);

  const [formData, setFormData] = useState({
    totalAmount: suggestedAmount || 0,
    numberOfInstallments: 12,
    firstPaymentDate: new Date().toISOString().split('T')[0],
    notes: '',
    setupMethod: PaymentPlanSetupMethod.Manual as PaymentPlanSetupMethod,
    monthlyAmount: 0,
    dayOfMonth: new Date().getDate()
  });
  const [monthlyAmountTouched, setMonthlyAmountTouched] = useState(false);
  const [dayOfMonthTouched, setDayOfMonthTouched] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Auto-derived defaults
  const computedInstallmentAmount = formData.totalAmount > 0 && formData.numberOfInstallments > 0
    ? formData.totalAmount / formData.numberOfInstallments
    : 0;

  // הארכת הנחה חודשית עד סוף התוכנית: הנחות חודשיות פעילות (לא הסתיימו לפני החודש הנוכחי)
  // שמסתיימות לפני התשלום האחרון בתוכנית. ההארכה מגדילה את ההנחה, ולכן מקטינה את הסכום המוצע.
  const planLastDate = formData.firstPaymentDate && formData.numberOfInstallments > 0
    ? (() => {
        const d = new Date(formData.firstPaymentDate);
        d.setMonth(d.getMonth() + formData.numberOfInstallments - 1);
        return toDateKey(d);
      })()
    : null;
  const currentMonthStart = toDateKey(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const extendableDiscounts = planLastDate
    ? (discounts ?? []).filter(d =>
        d.type === DiscountType.Monthly
        && !!d.endDate
        && toDateKey(d.endDate) >= currentMonthStart
        && toDateKey(d.endDate) < planLastDate)
    : [];
  const extensionNet = planLastDate
    ? extendableDiscounts.reduce((sum, d) => sum + d.amount * monthsBetween(d.endDate!, planLastDate), 0)
    : 0;
  // גירוס כמו בשרת: נטו + מע"מ מעוגל לאגורות
  const extensionGross = extensionNet + Math.round(extensionNet * (vatRate ?? 0)) / 100;
  const appliedExtension = extendDiscounts && extendableDiscounts.length > 0 ? extensionGross : 0;

  // הסכום המוצע מתעדכן לפי ההארכה, כל עוד המשתמש לא שינה אותו ידנית
  useEffect(() => {
    if (!isOpen || totalAmountTouched) return;
    const suggested = Math.max(0, Math.round(((suggestedAmount || 0) - appliedExtension) * 100) / 100);
    setFormData(prev => (prev.totalAmount === suggested ? prev : { ...prev, totalAmount: suggested }));
  }, [isOpen, totalAmountTouched, suggestedAmount, appliedExtension]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      setFormData({
        totalAmount: suggestedAmount || 0,
        numberOfInstallments: 12,
        firstPaymentDate: today.toISOString().split('T')[0],
        notes: '',
        setupMethod: PaymentPlanSetupMethod.Manual,
        monthlyAmount: 0,
        dayOfMonth: Math.min(today.getDate(), 28)
      });
      setMonthlyAmountTouched(false);
      setDayOfMonthTouched(false);
      setExtendDiscounts(false);
      setTotalAmountTouched(false);
      setErrors({});
      setView('form');
      setIframeUrl(null);
      setCurrentPlanId(null);
    }
  }, [isOpen, clientId, suggestedAmount]);

  // Sync auto-derived monthlyAmount when source fields change (if not manually touched)
  useEffect(() => {
    if (!monthlyAmountTouched) {
      setFormData(prev => ({ ...prev, monthlyAmount: computedInstallmentAmount }));
    }
  }, [computedInstallmentAmount, monthlyAmountTouched]);

  // Sync auto-derived dayOfMonth from firstPaymentDate (if not manually touched)
  useEffect(() => {
    if (!dayOfMonthTouched && formData.firstPaymentDate) {
      const day = new Date(formData.firstPaymentDate).getDate();
      setFormData(prev => ({ ...prev, dayOfMonth: Math.min(day, 28) }));
    }
  }, [formData.firstPaymentDate, dayOfMonthTouched]);

  // Cleanup polling on close
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.totalAmount || formData.totalAmount <= 0) {
      newErrors.totalAmount = 'יש להזין סכום תקין (גדול מאפס)';
    }
    if (!formData.numberOfInstallments || formData.numberOfInstallments < 1) {
      newErrors.numberOfInstallments = 'יש להזין מספר תשלומים תקין (לפחות 1)';
    }
    if (!formData.firstPaymentDate) {
      newErrors.firstPaymentDate = 'יש להזין תאריך תשלום ראשון';
    }

    if (formData.setupMethod === PaymentPlanSetupMethod.Cardcom) {
      if (!formData.monthlyAmount || formData.monthlyAmount <= 0) {
        newErrors.monthlyAmount = 'סכום חודשי חייב להיות גדול מאפס';
      }
      if (!formData.dayOfMonth || formData.dayOfMonth < 1 || formData.dayOfMonth > 28) {
        newErrors.dayOfMonth = 'יום חיוב חייב להיות בטווח 1-28 (מגבלת Cardcom)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const startPolling = (planId: number) => {
    if (pollingRef.current) window.clearInterval(pollingRef.current);
    pollingRef.current = window.setInterval(async () => {
      try {
        const plan = await paymentPlansApi.getById(planId);
        if (plan.cardcomMasterRecurringId) {
          if (pollingRef.current) {
            window.clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setView('success');
          toast.success('הוראת קבע הוקמה בהצלחה');
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 1500);
        } else if (plan.cardcomToken && view === 'iframe') {
          setView('finalizing');
        }
      } catch (error) {
        // ignore polling errors - just retry on next tick
      }
    }, 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const effectiveSetupMethod = formData.setupMethod;

    setLoading(true);
    try {
      const dto: CreatePaymentPlanDto = {
        clientId,
        totalAmount: formData.totalAmount,
        numberOfInstallments: formData.numberOfInstallments,
        firstPaymentDate: formData.firstPaymentDate,
        notes: formData.notes,
        setupMethod: effectiveSetupMethod,
        monthlyAmount: effectiveSetupMethod === PaymentPlanSetupMethod.Cardcom ? formData.monthlyAmount : undefined,
        dayOfMonth: effectiveSetupMethod === PaymentPlanSetupMethod.Cardcom ? formData.dayOfMonth : undefined,
        extendMonthlyDiscountsToPlanEnd: extendDiscounts && extendableDiscounts.length > 0
      };

      const plan = await paymentPlansApi.create(dto);

      if (effectiveSetupMethod === PaymentPlanSetupMethod.Cardcom) {
        // Get iframe URL
        const iframeResp = await paymentPlansApi.getCardcomIframeUrl(plan.id, {
          monthlyAmount: formData.monthlyAmount,
          dayOfMonth: formData.dayOfMonth
        });
        setIframeUrl(iframeResp.iframeUrl);
        setCurrentPlanId(plan.id);
        setView('iframe');
        startPolling(plan.id);
      } else {
        onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (pollingRef.current) {
      window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (view === 'iframe' || view === 'finalizing') {
      const confirmed = window.confirm(
        'הזנת פרטי האשראי בעיצומה. סגירה תאלץ את הלקוח להתחיל מחדש. האם להמשיך?'
      );
      if (!confirmed) return;
    }
    onClose();
  };

  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContent $wide={view === 'iframe'} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <ModalTitle>
              {view === 'form' && 'יצירת תוכנית תשלום'}
              {view === 'iframe' && 'הזנת פרטי כרטיס אשראי'}
              {view === 'finalizing' && 'מסיים הגדרת הוראת קבע...'}
              {view === 'success' && 'הוראת הקבע הוקמה בהצלחה'}
            </ModalTitle>
            <ClientBadge>{clientName}</ClientBadge>
          </div>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        {view === 'form' && (
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormGroup>
                <Label>
                  <RequiredStar>*</RequiredStar>
                  אופן הקמת תכנית התשלום
                </Label>
                <SetupMethodGroup>
                  <SetupMethodCard $selected={formData.setupMethod === PaymentPlanSetupMethod.Manual}>
                    <div>
                      <input
                        type="radio"
                        name="setupMethod"
                        value={PaymentPlanSetupMethod.Manual}
                        checked={formData.setupMethod === PaymentPlanSetupMethod.Manual}
                        onChange={() => setFormData({ ...formData, setupMethod: PaymentPlanSetupMethod.Manual })}
                      />
                      <SetupMethodTitle>ניהול ידני</SetupMethodTitle>
                    </div>
                    <SetupMethodDesc>רישום תשלומים ידני לכל חיוב</SetupMethodDesc>
                  </SetupMethodCard>
                  <SetupMethodCard $selected={formData.setupMethod === PaymentPlanSetupMethod.Cardcom}>
                    <div>
                      <input
                        type="radio"
                        name="setupMethod"
                        value={PaymentPlanSetupMethod.Cardcom}
                        checked={formData.setupMethod === PaymentPlanSetupMethod.Cardcom}
                        onChange={() => setFormData({ ...formData, setupMethod: PaymentPlanSetupMethod.Cardcom })}
                      />
                      <SetupMethodTitle>הוראת קבע (Cardcom)</SetupMethodTitle>
                    </div>
                    <SetupMethodDesc>חיוב חודשי אוטומטי דרך כרטיס אשראי</SetupMethodDesc>
                  </SetupMethodCard>
                </SetupMethodGroup>
              </FormGroup>

              <FormGroup>
                <Label>
                  <RequiredStar>*</RequiredStar>
                  סכום כולל (₪)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.totalAmount || ''}
                  onChange={(e) => {
                    setTotalAmountTouched(true);
                    setFormData({ ...formData, totalAmount: parseFloat(e.target.value) || 0 });
                  }}
                  placeholder="0.00"
                  hasError={!!errors.totalAmount}
                  required
                />
                {errors.totalAmount ? (
                  <ErrorMessage>{errors.totalAmount}</ErrorMessage>
                ) : suggestedAmount !== undefined && (
                  <HelperText>
                    יתרה לתשלום: {formatCurrency(suggestedAmount)}
                    {appliedExtension > 0 && ` · אחרי הארכת ההנחה: ${formatCurrency(Math.max(0, suggestedAmount - appliedExtension))}`}
                  </HelperText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  <RequiredStar>*</RequiredStar>
                  מספר תשלומים
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={formData.numberOfInstallments || ''}
                  onChange={(e) => setFormData({ ...formData, numberOfInstallments: parseInt(e.target.value) || 0 })}
                  placeholder="12"
                  hasError={!!errors.numberOfInstallments}
                  required
                />
                {errors.numberOfInstallments && <ErrorMessage>{errors.numberOfInstallments}</ErrorMessage>}
              </FormGroup>

              {extendableDiscounts.length > 0 && planLastDate && (
                <ExtendDiscountBox>
                  <input
                    type="checkbox"
                    checked={extendDiscounts}
                    onChange={(e) => setExtendDiscounts(e.target.checked)}
                  />
                  <ExtendDiscountText>
                    <strong>הארך את ההנחה החודשית עד סוף התוכנית</strong>
                    {extendableDiscounts.map(d => (
                      <span key={d.id}>
                        {formatCurrency(d.amount)} לחודש (לפני מע"מ), מסתיימת ב-{formatDate(d.endDate!)} — תוארך עד {formatDate(planLastDate)}
                      </span>
                    ))}
                    {extendDiscounts && (
                      <span>תוספת הנחה: {formatCurrency(extensionGross)} כולל מע"מ — הסכום הכולל עודכן בהתאם</span>
                    )}
                  </ExtendDiscountText>
                </ExtendDiscountBox>
              )}

              {computedInstallmentAmount > 0 && formData.setupMethod === PaymentPlanSetupMethod.Manual && (
                <CalculatedValue>
                  <CalculatedLabel>סכום לתשלום</CalculatedLabel>
                  <CalculatedAmount>{formatCurrency(computedInstallmentAmount)}</CalculatedAmount>
                </CalculatedValue>
              )}

              <FormGroup>
                <Label>
                  <RequiredStar>*</RequiredStar>
                  תאריך תשלום ראשון
                </Label>
                <Input
                  type="date"
                  value={formData.firstPaymentDate}
                  onChange={(e) => setFormData({ ...formData, firstPaymentDate: e.target.value })}
                  hasError={!!errors.firstPaymentDate}
                  required
                />
                {errors.firstPaymentDate && <ErrorMessage>{errors.firstPaymentDate}</ErrorMessage>}
              </FormGroup>

              {formData.setupMethod === PaymentPlanSetupMethod.Cardcom && (
                <FormRow>
                  <FormGroup>
                    <Label>
                      <RequiredStar>*</RequiredStar>
                      סכום חודשי (₪)
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.monthlyAmount || ''}
                      onChange={(e) => {
                        setMonthlyAmountTouched(true);
                        setFormData({ ...formData, monthlyAmount: parseFloat(e.target.value) || 0 });
                      }}
                      hasError={!!errors.monthlyAmount}
                    />
                    {errors.monthlyAmount ? (
                      <ErrorMessage>{errors.monthlyAmount}</ErrorMessage>
                    ) : (
                      <HelperText>ברירת מחדל: {formatCurrency(computedInstallmentAmount)}</HelperText>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <Label>
                      <RequiredStar>*</RequiredStar>
                      יום חיוב בחודש (1-28)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="28"
                      value={formData.dayOfMonth || ''}
                      onChange={(e) => {
                        setDayOfMonthTouched(true);
                        setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) || 0 });
                      }}
                      hasError={!!errors.dayOfMonth}
                    />
                    {errors.dayOfMonth ? (
                      <ErrorMessage>{errors.dayOfMonth}</ErrorMessage>
                    ) : (
                      <HelperText>נגזר מתאריך התשלום הראשון</HelperText>
                    )}
                  </FormGroup>
                </FormRow>
              )}

              <FormGroup>
                <Label>הערות</Label>
                <TextArea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="הערות נוספות לגבי תוכנית התשלום..."
                />
              </FormGroup>
            </ModalBody>

            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={handleClose}>
                ביטול
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? (formData.setupMethod === PaymentPlanSetupMethod.Cardcom ? 'יוצר קישור...' : 'יוצר...')
                  : (formData.setupMethod === PaymentPlanSetupMethod.Cardcom ? 'המשך לסליקה' : 'יצירה')}
              </Button>
            </ButtonContainer>
          </form>
        )}

        {view === 'iframe' && iframeUrl && (
          <IframeContainer>
            <IframeBanner>
              <strong>שלב 2:</strong> הזנת פרטי כרטיס האשראי בעמוד Cardcom המאובטח. לאחר סיום ההזנה, הוראת הקבע תוקם אוטומטית.
            </IframeBanner>
            <Iframe src={iframeUrl} title="Cardcom" allow="payment" />
          </IframeContainer>
        )}

        {view === 'finalizing' && (
          <StatusBox>
            <Spinner />
            <div>מסיים הגדרת הוראת קבע מול Cardcom...</div>
            <HelperText style={{ marginTop: 12 }}>זה עשוי לקחת מספר שניות</HelperText>
          </StatusBox>
        )}

        {view === 'success' && (
          <StatusBox>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>הוראת הקבע הוקמה בהצלחה!</div>
            <HelperText style={{ marginTop: 8 }}>החיוב הראשון יבוצע אוטומטית ביום שנקבע</HelperText>
          </StatusBox>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreatePaymentPlanModal;
